import React from 'react';
import useDevice, { Device } from '../../hooks/device.hooks';

const INITIAL_ITEMS = {
  [Device.MOBILE]: 3,
  [Device.DESKTOP]: 5,
};

const ITEMS_PER_PAGE = {
  [Device.MOBILE]: 5,
  [Device.DESKTOP]: 10,
};

export const usePagination = <Type = any>(
  fetch: (offset: number, limit: number) => Promise<Type[]>,
) => {
  const [items, setItems] = React.useState<Type[]>([]);
  const [hasMore, setHasMore] = React.useState(true);
  const [currentPage, setCurrentPage] = React.useState(1);
  const device = useDevice();

  const getLimit = React.useCallback(
    (page: number) => {
      if (!device) return ITEMS_PER_PAGE.mobile;

      let limit = ITEMS_PER_PAGE[device];

      if (page === 1) {
        limit = INITIAL_ITEMS[device];
      }

      return limit;
    },
    [device],
  );

  const getPreviousResultsCount = React.useCallback((): number => {
    let results = 0;

    for (let i = 1; i <= currentPage - 1; i += 1) {
      results += getLimit(i);
    }

    return results;
  }, [currentPage]);

  const loadItems = async () => {
    if (!device) return;
    const data = await fetch(getPreviousResultsCount(), getLimit(currentPage));

    if (
      data.length === 0 ||
      (device === 'mobile' && currentPage === 1 && data.length < 3) ||
      (device === 'mobile' && currentPage > 1 && data.length < 5) ||
      (device === 'desktop' && currentPage === 1 && data.length < 5) ||
      (device === 'desktop' && currentPage > 1 && data.length < 10)
    ) {
      setHasMore(false);
    }

    setItems((prevItems) => [...prevItems, ...data]);
  };

  const loadMore = React.useCallback(async () => {
    setCurrentPage(currentPage + 1);
  }, [currentPage]);

  React.useEffect(() => {
    if (!device) return;
    setItems([]);
    setCurrentPage(1);
  }, [device]);

  React.useEffect(() => {
    if (!device) return;
    loadItems();
  }, [device, currentPage]);

  return { loadMore, items, hasMore, setHasMore };
};
